import { useState } from 'react';
import { IconPlus, IconTrash, IconWrench } from '../../components/icons';
import { Badge, Card, EmptyState, ErrorMessage, Field, Loading, Modal, ModuleHeader, SearchSelect, Tabs, Toggle, useAsync, useToast } from '../../components/ui';
import { RowActions } from '../../components/RowActions';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { formatDuration, formatPercent } from '../../lib/format';

/**
 * Administration sämtlicher fachlicher Parameter (Kap. 9 des Konzepts) –
 * Kritikalität/SLA, Gewerke, Qualifikationen und Checklisten.
 */
export function CatalogsPage() {
  const { can } = useAuth();
  const { notify } = useToast();
  const [tab, setTab] = useState<'sla' | 'kategorien' | 'wartung' | 'skills' | 'checklisten' | 'overrides'>('sla');
  const [modal, setModal] = useState<string | null>(null);

  const criticality = useAsync(() => api.get('/api/catalogs/criticality-levels'), [tab]);
  const categories = useAsync(() => api.get('/api/catalogs/categories'), [tab]);
  const skills = useAsync(() => api.get('/api/catalogs/skills'), [tab]);
  const checklists = useAsync(() => api.get('/api/catalogs/checklist-templates'), [tab]);
  const overrides = useAsync(() => api.get('/api/catalogs/sla-overrides').catch(() => []), [tab]);
  const maintenance = useAsync(
    () => api.get('/api/catalogs/maintenance-categories', { includeInactive: 'true' }).catch(() => []),
    [tab],
  );

  const mayEdit = can('admin.catalogs.manage');

  const reloadAll = () => {
    criticality.reload();
    categories.reload();
    skills.reload();
    checklists.reload();
    overrides.reload();
    maintenance.reload();
  };

  return (
    <div className="stack">
      <ModuleHeader
        icon={<IconWrench width={22} height={22} />}
        title="Kataloge, Kritikalität & SLA"
        subtitle="Alle fachlichen Parameter sind hier pflegbar – ohne Eingriff in den Programmcode. Änderungen an SLA-Zeiten wirken sich auf neu angelegte Aufträge aus."
        pill="Kataloge"
        tone="warning"
        actions={
          mayEdit ? (
            <button className="btn primary" onClick={() => setModal(tab)}>
              <IconPlus width={15} height={15} /> Eintrag anlegen
            </button>
          ) : undefined
        }
      />

      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { key: 'sla', label: 'Kritikalität & SLA', count: criticality.data?.length },
          { key: 'overrides', label: 'Abweichende SLA', count: overrides.data?.length },
          { key: 'kategorien', label: 'Gewerke', count: categories.data?.length },
          { key: 'wartung', label: 'Wartungskategorien', count: maintenance.data?.length },
          { key: 'skills', label: 'Qualifikationen', count: skills.data?.length },
          { key: 'checklisten', label: 'Checklisten', count: checklists.data?.length },
        ]}
      />

      {tab === 'sla' && (
        <Card
          title="Kritikalitätsstufen"
          subtitle="Reaktions- und Erledigungszeiten sowie Eskalationsverhalten je Stufe"
          noBody
        >
          {criticality.loading ? (
            <Loading />
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Stufe</th>
                    <th>Bezeichnung</th>
                    <th>Beispiel</th>
                    <th className="num">Reaktionszeit</th>
                    <th className="num">Erledigungszeit</th>
                    <th className="num">Warnung ab</th>
                    <th>Eskalation</th>
                    <th className="num">Aufträge</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {(criticality.data ?? []).map((level: any) => (
                    <tr key={level.id}>
                      <td>
                        <Badge color={level.color}>{level.level}</Badge>
                      </td>
                      <td>
                        <strong>{level.name}</strong>
                        {level.notify_standby ? <div className="small subtle">Rufbereitschaft wird alarmiert</div> : null}
                      </td>
                      <td className="small subtle truncate" style={{ maxWidth: 260 }}>
                        {level.example ?? '–'}
                      </td>
                      <td className="num">{formatDuration(level.response_minutes)}</td>
                      <td className="num">{formatDuration(level.resolution_minutes)}</td>
                      <td className="num small">{formatPercent(level.escalate_after_pct)}</td>
                      <td className="small subtle truncate" style={{ maxWidth: 200 }}>
                        {level.escalation_note ?? '–'}
                      </td>
                      <td className="num">{level.task_count}</td>
                      <td className="actions">
                        {mayEdit && (
                          <button className="btn sm" onClick={() => setModal(`edit-sla-${level.id}`)}>
                            Bearbeiten
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {tab === 'overrides' && (
        <Card
          title="Abweichende SLA je Kunde oder Objekt"
          subtitle="Vertraglich vereinbarte Fristen haben Vorrang vor der allgemeinen Kritikalitätsstufe"
          noBody
        >
          {!overrides.data?.length ? (
            <EmptyState title="Keine abweichenden SLA hinterlegt" />
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Kritikalität</th>
                    <th>Gilt für</th>
                    <th className="num">Reaktionszeit</th>
                    <th className="num">Erledigungszeit</th>
                    <th>Hinweis</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {overrides.data.map((override: any) => (
                    <tr key={override.id}>
                      <td>
                        {override.criticality_name}
                      </td>
                      <td>
                        {override.property_name ? (
                          <Badge variant="info">Objekt: {override.property_name}</Badge>
                        ) : (
                          <Badge variant="primary">Kunde: {override.customer_name}</Badge>
                        )}
                      </td>
                      <td className="num">{formatDuration(override.response_minutes)}</td>
                      <td className="num">{formatDuration(override.resolution_minutes)}</td>
                      <td className="small subtle">{override.note ?? '–'}</td>
                      <td className="actions">
                        {mayEdit && (
                          <button
                            className="btn sm ghost"
                            onClick={async () => {
                              await api.delete(`/api/catalogs/sla-overrides/${override.id}`);
                              overrides.reload();
                              notify('Eintrag gelöscht.', 'success');
                            }}
                          >
                            Löschen
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {tab === 'kategorien' && (
        <Card title="Kategorien & Gewerke" subtitle="Bestimmen Standarddauer und erforderliche Qualifikation" noBody>
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Bezeichnung</th>
                  <th>Gewerk</th>
                  <th>Erforderliche Qualifikation</th>
                  <th className="num">Standarddauer</th>
                  <th className="num">Aufträge</th>
                  <th>Status</th>
                  {mayEdit && <th className="actions">Aktionen</th>}
                </tr>
              </thead>
              <tbody>
                {(categories.data ?? []).map((category: any) => (
                  <tr key={category.id} style={category.is_active ? undefined : { opacity: 0.55 }}>
                    <td>
                      <Badge color={category.color}>{category.name}</Badge>
                      {category.parent_name && <div className="small subtle">↳ unter {category.parent_name}</div>}
                    </td>
                    <td className="small">{category.trade ?? '–'}</td>
                    <td className="small">{category.required_skill_name ?? '–'}</td>
                    <td className="num">{formatDuration(category.default_minutes)}</td>
                    <td className="num">{category.task_count}</td>
                    <td>
                      <Badge variant={category.is_active ? 'success' : ''}>
                        {category.is_active ? 'Aktiv' : 'Inaktiv'}
                      </Badge>
                    </td>
                    {mayEdit && (
                      <td className="actions">
                        <RowActions
                          label={category.name}
                          onEdit={() => setModal(`edit-kategorien-${category.id}`)}
                          deletePath={`/api/catalogs/categories/${category.id}`}
                          confirmText={
                            category.task_count > 0
                              ? `Gewerk „${category.name}“ wird von ${category.task_count} Aufträgen verwendet und deshalb nur stillgelegt. Fortfahren?`
                              : `Gewerk „${category.name}“ löschen?`
                          }
                          onDeleted={reloadAll}
                        />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {tab === 'wartung' && (
        <Card
          title="Wartungskategorien"
          subtitle="Was geprüft wird – im Unterschied zum Gewerk, das beschreibt, wer arbeitet"
          noBody
        >
          {maintenance.loading ? (
            <Loading />
          ) : (maintenance.data ?? []).length === 0 ? (
            <div className="card-body">
              <EmptyState
                title="Noch keine Wartungskategorie"
                hint="Kategorien bündeln wiederkehrende Prüfungen und bringen ihr übliches Intervall mit."
              />
            </div>
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Bezeichnung</th>
                    <th>Schlüssel</th>
                    <th>Übliches Intervall</th>
                    <th className="num">Vorlauf</th>
                    <th>Grundlage</th>
                    <th className="num">Pläne</th>
                    <th>Status</th>
                    {mayEdit && <th className="actions">Aktionen</th>}
                  </tr>
                </thead>
                <tbody>
                  {(maintenance.data ?? []).map((category: any) => (
                    <tr key={category.id} style={category.is_active ? undefined : { opacity: 0.55 }}>
                      <td>
                        <Badge color={category.color}>{category.name}</Badge>
                        {category.description && <div className="small subtle">{category.description}</div>}
                      </td>
                      <td className="mono small">{category.key}</td>
                      <td className="small">
                        alle {category.default_interval_value}{' '}
                        {unitLabel(category.default_interval_unit, category.default_interval_value)}
                      </td>
                      <td className="num small subtle">{category.default_lead_days} Tage</td>
                      <td className="small subtle">{category.legal_basis ?? '–'}</td>
                      <td className="num">{category.plan_count}</td>
                      <td>
                        <Badge variant={category.is_active ? 'success' : ''}>
                          {category.is_active ? 'Aktiv' : 'Stillgelegt'}
                        </Badge>
                      </td>
                      {mayEdit && (
                        <td className="actions">
                          {category.is_active ? (
                            <RowActions
                              label={category.name}
                              onEdit={() => setModal(`edit-wartung-${category.id}`)}
                              deletePath={`/api/catalogs/maintenance-categories/${category.id}`}
                              confirmText={
                                category.plan_count > 0
                                  ? `„${category.name}“ wird von ${category.plan_count} Wartungsplan/-plänen verwendet und deshalb nur stillgelegt. Fortfahren?`
                                  : `Wartungskategorie „${category.name}“ löschen?`
                              }
                              onDeleted={reloadAll}
                            />
                          ) : (
                            <button
                              className="btn sm"
                              onClick={async () => {
                                await api.post(`/api/catalogs/maintenance-categories/${category.id}/reactivate`);
                                notify('Wartungskategorie wieder aktiv.', 'success');
                                reloadAll();
                              }}
                            >
                              Reaktivieren
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {tab === 'skills' && (
        <Card title="Qualifikationen" subtitle="Grundlage für automatische Zuweisungsvorschläge" noBody>
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Bezeichnung</th>
                  <th>Schlüssel</th>
                  <th>Beschreibung</th>
                  <th className="num">Mitarbeitende</th>
                  {mayEdit && <th className="actions">Aktionen</th>}
                </tr>
              </thead>
              <tbody>
                {(skills.data ?? []).map((skill: any) => (
                  <tr key={skill.id}>
                    <td>
                      <strong>{skill.name}</strong>
                    </td>
                    <td className="mono small">{skill.key}</td>
                    <td className="small subtle">{skill.description ?? '–'}</td>
                    <td className="num">{skill.user_count}</td>
                    {mayEdit && (
                      <td className="actions">
                        <RowActions
                          label={skill.name}
                          onEdit={() => setModal(`edit-skills-${skill.id}`)}
                          deletePath={`/api/catalogs/skills/${skill.id}`}
                          forceOnConflict
                          onDeleted={reloadAll}
                        />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {tab === 'checklisten' && (
        <div className="grid cols-2">
          {(checklists.data ?? []).map((template: any) => (
            <Card
              key={template.id}
              title={
                <span className="row" style={{ gap: 6, alignItems: 'center' }}>
                  {template.name}
                </span>
              }
              subtitle={template.description}
              actions={
                mayEdit ? (
                  <RowActions
                    label={template.name}
                    onEdit={() => setModal(`edit-checklisten-${template.id}`)}
                    deletePath={`/api/catalogs/checklist-templates/${template.id}`}
                    confirmText={`Checkliste „${template.name}“ löschen? Laufende Aufträge behalten ihre Punkte – sie wurden beim Anlegen kopiert.`}
                    onDeleted={reloadAll}
                  />
                ) : undefined
              }
            >
              <ol style={{ margin: 0, paddingLeft: 18 }}>
                {(template.items ?? []).map((item: any) => (
                  <li key={item.id} style={{ fontSize: 13, marginBottom: 4 }}>
                    {item.label}{' '}
                    <Badge>{inputTypeLabel(item.input_type)}</Badge>
                    {item.is_required ? <span style={{ color: 'var(--danger)' }}> *</span> : null}
                  </li>
                ))}
              </ol>
            </Card>
          ))}
          {!checklists.data?.length && (
            <Card>
              <EmptyState title="Keine Checklisten-Vorlagen" hint="Checklisten können je Gewerk hinterlegt werden." />
            </Card>
          )}
        </div>
      )}

      {modal?.startsWith('edit-sla-') && (
        <CriticalityModal
          level={(criticality.data ?? []).find((entry: any) => String(entry.id) === modal.replace('edit-sla-', ''))}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            reloadAll();
            notify('Kritikalitätsstufe gespeichert.', 'success');
          }}
        />
      )}

      {modal === 'sla' && (
        <CriticalityModal
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            reloadAll();
            notify('Kritikalitätsstufe angelegt.', 'success');
          }}
        />
      )}

      {modal === 'overrides' && (
        <OverrideModal
          criticalityLevels={criticality.data ?? []}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            overrides.reload();
            notify('Abweichende SLA gespeichert.', 'success');
          }}
        />
      )}

      {(modal === 'kategorien' || modal?.startsWith('edit-kategorien-')) && (
        <CategoryModal
          category={findById(categories.data, modal, 'edit-kategorien-')}
          skills={skills.data ?? []}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            reloadAll();
            notify('Gewerk gespeichert.', 'success');
          }}
        />
      )}

      {(modal === 'skills' || modal?.startsWith('edit-skills-')) && (
        <SkillModal
          skill={findById(skills.data, modal, 'edit-skills-')}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            reloadAll();
            notify('Qualifikation gespeichert.', 'success');
          }}
        />
      )}

      {(modal === 'wartung' || modal?.startsWith('edit-wartung-')) && (
        <MaintenanceCategoryModal
          category={findById(maintenance.data, modal, 'edit-wartung-')}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            reloadAll();
            notify('Wartungskategorie gespeichert.', 'success');
          }}
        />
      )}

      {(modal === 'checklisten' || modal?.startsWith('edit-checklisten-')) && (
        <ChecklistModal
          template={findById(checklists.data, modal, 'edit-checklisten-')}
          categories={categories.data ?? []}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            reloadAll();
            notify('Checkliste gespeichert.', 'success');
          }}
        />
      )}
    </div>
  );
}

function CriticalityModal({
  level,
  onClose,
  onSaved,
}: {
  level?: any;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    key: level?.key ?? '',
    name: level?.name ?? '',
    level: String(level?.level ?? 3),
    color: level?.color ?? '#2563eb',
    example: level?.example ?? '',
    responseMinutes: level?.response_minutes != null ? String(level.response_minutes) : '',
    resolutionMinutes: level?.resolution_minutes != null ? String(level.resolution_minutes) : '',
    escalateAfterPct: String(level?.escalate_after_pct ?? 80),
    escalationNote: level?.escalation_note ?? '',
    notifyStandby: !!level?.notify_standby,
    isActive: level ? !!level.is_active : true,
  });
  const [error, setError] = useState<unknown>(null);
  const set = (key: keyof typeof form, value: any) => setForm((current) => ({ ...current, [key]: value }));

  return (
    <Modal
      title={level ? `Kritikalität bearbeiten – ${level.name}` : 'Kritikalitätsstufe anlegen'}
      onClose={onClose}
      size="wide"
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button
            className="btn primary"
            disabled={!form.name || !form.key}
            onClick={async () => {
              const payload = {
                key: form.key,
                name: form.name,
                level: Number(form.level),
                color: form.color,
                example: form.example,
                responseMinutes: form.responseMinutes ? Number(form.responseMinutes) : null,
                resolutionMinutes: form.resolutionMinutes ? Number(form.resolutionMinutes) : null,
                escalateAfterPct: Number(form.escalateAfterPct),
                escalationNote: form.escalationNote,
                notifyStandby: form.notifyStandby,
                isActive: form.isActive,
              };
              try {
                if (level) await api.patch(`/api/catalogs/criticality-levels/${level.id}`, payload);
                else await api.post('/api/catalogs/criticality-levels', payload);
                onSaved();
              } catch (caught) {
                setError(caught);
              }
            }}
          >
            Speichern
          </button>
        </>
      }
    >
      <div className="stack">
        <ErrorMessage error={error} />
        <div className="form-grid">
          <Field label="Bezeichnung" required>
            <input value={form.name} onChange={(event) => set('name', event.target.value)} autoFocus />
          </Field>
          <Field label="Schlüssel" required>
            <input value={form.key} disabled={!!level} onChange={(event) => set('key', event.target.value)} />
          </Field>
          <Field label="Stufe (1 = höchste)" required>
            <input type="number" min="1" max="99" value={form.level} onChange={(event) => set('level', event.target.value)} />
          </Field>
          <Field label="Farbe">
            <input type="text" value={form.color} onChange={(event) => set('color', event.target.value)} placeholder="#dc2626" />
          </Field>
          <Field label="Reaktionszeit (Minuten)" hint="Leer = keine Frist">
            <input type="number" min="0" value={form.responseMinutes} onChange={(event) => set('responseMinutes', event.target.value)} />
          </Field>
          <Field label="Erledigungszeit (Minuten)" hint="Leer = nach Kapazität">
            <input type="number" min="0" value={form.resolutionMinutes} onChange={(event) => set('resolutionMinutes', event.target.value)} />
          </Field>
          <Field label="Warnung ab (% der Frist)">
            <input type="number" min="10" max="100" value={form.escalateAfterPct} onChange={(event) => set('escalateAfterPct', event.target.value)} />
          </Field>
        </div>
        <Field label="Beispiel">
          <input value={form.example} onChange={(event) => set('example', event.target.value)} />
        </Field>
        <Field label="Eskalationsregel">
          <input value={form.escalationNote} onChange={(event) => set('escalationNote', event.target.value)} />
        </Field>
        <div className="row" style={{ gap: 20 }}>
          <Toggle checked={form.notifyStandby} onChange={(value) => set('notifyStandby', value)} label="Rufbereitschaft alarmieren" />
          <Toggle checked={form.isActive} onChange={(value) => set('isActive', value)} label="Aktiv" />
        </div>
      </div>
    </Modal>
  );
}

function OverrideModal({
  criticalityLevels,
  onClose,
  onSaved,
}: {
  criticalityLevels: any[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const customers = useAsync(() => api.get('/api/customers').catch(() => []), []);
  const properties = useAsync(() => api.get('/api/properties').catch(() => []), []);
  const [form, setForm] = useState({
    criticalityId: '',
    scope: 'customer' as 'customer' | 'property',
    targetId: '',
    responseMinutes: '',
    resolutionMinutes: '',
    note: '',
  });
  const [error, setError] = useState<unknown>(null);

  return (
    <Modal
      title="Abweichende SLA hinterlegen"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button
            className="btn primary"
            disabled={!form.criticalityId || !form.targetId}
            onClick={async () => {
              try {
                await api.post('/api/catalogs/sla-overrides', {
                  criticalityId: Number(form.criticalityId),
                  customerId: form.scope === 'customer' ? Number(form.targetId) : null,
                  propertyId: form.scope === 'property' ? Number(form.targetId) : null,
                  responseMinutes: form.responseMinutes ? Number(form.responseMinutes) : null,
                  resolutionMinutes: form.resolutionMinutes ? Number(form.resolutionMinutes) : null,
                  note: form.note || undefined,
                });
                onSaved();
              } catch (caught) {
                setError(caught);
              }
            }}
          >
            Speichern
          </button>
        </>
      }
    >
      <div className="stack">
        <ErrorMessage error={error} />
        <Field label="Kritikalitätsstufe" required>
          <SearchSelect
            value={String(form.criticalityId ?? '')}
            onChange={(wert) => setForm({ ...form, criticalityId: wert })}
            options={(criticalityLevels ?? []).map((level: any) => ({ value: String(level.id), label: String(level.name) }))}
          />
        </Field>
        <Field label="Gilt für" required>
          <select value={form.scope} onChange={(event) => setForm({ ...form, scope: event.target.value as any, targetId: '' })}>
            <option value="customer">Eigentümer/Verwalter</option>
            <option value="property">Einzelnes Objekt</option>
          </select>
        </Field>
        <Field label={form.scope === 'customer' ? 'Eigentümer/Verwalter' : 'Objekt'} required>
          <SearchSelect
            value={String(form.targetId ?? '')}
            onChange={(wert) => setForm({ ...form, targetId: wert })}
            options={(form.scope === 'customer' ? customers.data ?? [] : properties.data ?? []).map((entry: any) => ({ value: String(entry.id), label: String(entry.name) }))}
          />
        </Field>
        <div className="form-grid">
          <Field label="Reaktionszeit (Min.)">
            <input type="number" value={form.responseMinutes} onChange={(event) => setForm({ ...form, responseMinutes: event.target.value })} />
          </Field>
          <Field label="Erledigungszeit (Min.)">
            <input type="number" value={form.resolutionMinutes} onChange={(event) => setForm({ ...form, resolutionMinutes: event.target.value })} />
          </Field>
        </div>
        <Field label="Hinweis" hint="z. B. Vertragsreferenz">
          <input value={form.note} onChange={(event) => setForm({ ...form, note: event.target.value })} />
        </Field>
      </div>
    </Modal>
  );
}

function CategoryModal({
  category,
  skills,
  onClose,
  onSaved,
}: {
  category?: any;
  skills: any[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    key: category?.key ?? '',
    name: category?.name ?? '',
    trade: category?.trade ?? '',
    color: category?.color ?? '#2563eb',
    defaultMinutes: category?.default_minutes != null ? String(category.default_minutes) : '',
    requiredSkillId: category?.required_skill_id ? String(category.required_skill_id) : '',
    isActive: category ? !!category.is_active : true,
  });
  const [error, setError] = useState<unknown>(null);

  return (
    <Modal
      title={category ? `Gewerk bearbeiten – ${category.name}` : 'Kategorie / Gewerk anlegen'}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button
            className="btn primary"
            disabled={!form.key || !form.name}
            onClick={async () => {
              try {
                const payload = {
                  ...form,
                  defaultMinutes: form.defaultMinutes ? Number(form.defaultMinutes) : null,
                  requiredSkillId: form.requiredSkillId ? Number(form.requiredSkillId) : null,
                };
                if (category) await api.patch(`/api/catalogs/categories/${category.id}`, payload);
                else await api.post('/api/catalogs/categories', payload);
                onSaved();
              } catch (caught) {
                setError(caught);
              }
            }}
          >
            Speichern
          </button>
        </>
      }
    >
      <div className="stack">
        <ErrorMessage error={error} />
        <div className="form-grid">
          <Field label="Bezeichnung" required>
            <input
              value={form.name}
              autoFocus
              onChange={(event) =>
                setForm({
                  ...form,
                  name: event.target.value,
                  key: form.key || event.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '_'),
                })
              }
            />
          </Field>
          <Field label="Schlüssel" required hint={category ? 'Nach dem Anlegen unveränderlich.' : undefined}>
            <input
              value={form.key}
              disabled={!!category}
              onChange={(event) => setForm({ ...form, key: event.target.value })}
            />
          </Field>
          <Field label="Gewerk">
            <input value={form.trade} onChange={(event) => setForm({ ...form, trade: event.target.value })} />
          </Field>
          <Field label="Farbe">
            <input value={form.color} onChange={(event) => setForm({ ...form, color: event.target.value })} />
          </Field>
          <Field label="Standarddauer (Min.)">
            <input type="number" value={form.defaultMinutes} onChange={(event) => setForm({ ...form, defaultMinutes: event.target.value })} />
          </Field>
          <Field label="Erforderliche Qualifikation">
            <SearchSelect
              value={String(form.requiredSkillId ?? '')}
              onChange={(wert) => setForm({ ...form, requiredSkillId: wert })}
              options={(skills ?? []).map((skill: any) => ({ value: String(skill.id), label: String(skill.name) }))}
              placeholder="Keine"
            />
          </Field>
        </div>
        <Toggle
          checked={form.isActive}
          onChange={(value) => setForm({ ...form, isActive: value })}
          label="Aktiv – nur aktive Gewerke stehen bei einer neuen Meldung zur Wahl"
        />
      </div>
    </Modal>
  );
}

function SkillModal({ skill, onClose, onSaved }: { skill?: any; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState({
    key: skill?.key ?? '',
    name: skill?.name ?? '',
    description: skill?.description ?? '',
  });
  const [error, setError] = useState<unknown>(null);

  return (
    <Modal
      title={skill ? `Qualifikation bearbeiten – ${skill.name}` : 'Qualifikation anlegen'}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button
            className="btn primary"
            disabled={!form.key || !form.name}
            onClick={async () => {
              try {
                if (skill) await api.patch(`/api/catalogs/skills/${skill.id}`, { name: form.name, description: form.description });
                else await api.post('/api/catalogs/skills', form);
                onSaved();
              } catch (caught) {
                setError(caught);
              }
            }}
          >
            Speichern
          </button>
        </>
      }
    >
      <div className="stack">
        <ErrorMessage error={error} />
        <Field label="Bezeichnung" required>
          <input
            value={form.name}
            autoFocus
            onChange={(event) =>
              setForm({
                ...form,
                name: event.target.value,
                key: form.key || event.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '_'),
              })
            }
          />
        </Field>
        <Field label="Schlüssel" required hint={skill ? 'Nach dem Anlegen unveränderlich.' : undefined}>
          <input
            value={form.key}
            disabled={!!skill}
            onChange={(event) => setForm({ ...form, key: event.target.value })}
          />
        </Field>
        <Field label="Beschreibung">
          <input value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} />
        </Field>
      </div>
    </Modal>
  );
}

/**
 * Checklisten-Vorlage.
 *
 * Punkte werden in einem Rutsch bearbeitet statt einzeln über Dialoge: Eine
 * Checkliste liest man als Ganzes, und die Reihenfolge ist Teil der Aussage.
 */
function ChecklistModal({
  template,
  categories,
  onClose,
  onSaved,
}: {
  template?: any;
  categories: any[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    name: template?.name ?? '',
    description: template?.description ?? '',
    categoryId: template?.category_id ? String(template.category_id) : '',
  });
  const [items, setItems] = useState<{ label: string; inputType: string; isRequired: boolean }[]>(
    (template?.items ?? []).map((item: any) => ({
      label: item.label,
      inputType: item.input_type ?? 'check',
      isRequired: !!item.is_required,
    })),
  );
  const [error, setError] = useState<unknown>(null);

  const setItem = (index: number, patch: Partial<(typeof items)[number]>) =>
    setItems((current) => current.map((item, i) => (i === index ? { ...item, ...patch } : item)));

  const move = (index: number, delta: number) =>
    setItems((current) => {
      const next = [...current];
      const target = index + delta;
      if (target < 0 || target >= next.length) return current;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });

  return (
    <Modal
      title={template ? `Checkliste bearbeiten – ${template.name}` : 'Checkliste anlegen'}
      size="wide"
      onClose={onClose}
      footer={
        <>
          <span className="small subtle" style={{ marginRight: 'auto' }}>
            {items.length} Punkt(e)
          </span>
          <button className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button
            className="btn primary"
            disabled={!form.name || items.length === 0 || items.some((item) => !item.label.trim())}
            onClick={async () => {
              try {
                const head = {
                  name: form.name,
                  description: form.description || null,
                  categoryId: form.categoryId ? Number(form.categoryId) : null,
                };
                if (template) {
                  await api.patch(`/api/catalogs/checklist-templates/${template.id}`, head);
                  await api.put(`/api/catalogs/checklist-templates/${template.id}/items`, { items });
                } else {
                  await api.post('/api/catalogs/checklist-templates', { ...head, items });
                }
                onSaved();
              } catch (caught) {
                setError(caught);
              }
            }}
          >
            Speichern
          </button>
        </>
      }
    >
      <div className="stack">
        <ErrorMessage error={error} />
        <div className="form-grid">
          <Field label="Bezeichnung" required>
            <input value={form.name} autoFocus onChange={(event) => setForm({ ...form, name: event.target.value })} />
          </Field>
          <Field label="Gewerk" hint="Legt fest, bei welchen Aufträgen die Liste vorgeschlagen wird.">
            <SearchSelect
              value={String(form.categoryId ?? '')}
              onChange={(wert) => setForm({ ...form, categoryId: wert })}
              options={(categories ?? []).map((category: any) => ({ value: String(category.id), label: String(category.name) }))}
              placeholder="Ohne Zuordnung"
            />
          </Field>
        </div>
        <Field label="Beschreibung">
          <input value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} />
        </Field>

        <span className="section-title">Punkte</span>
        <div className="stack" style={{ gap: 6 }}>
          {items.map((item, index) => (
            <div key={index} className="row" style={{ gap: 6, alignItems: 'center' }}>
              <span className="small subtle" style={{ width: 18 }}>
                {index + 1}.
              </span>
              <input
                value={item.label}
                onChange={(event) => setItem(index, { label: event.target.value })}
                placeholder="Was ist zu prüfen?"
                style={{ flex: 1 }}
              />
              <select
                value={item.inputType}
                onChange={(event) => setItem(index, { inputType: event.target.value })}
                style={{ width: 110 }}
              >
                <option value="check">Haken</option>
                <option value="text">Text</option>
                <option value="number">Zahl</option>
                <option value="photo">Foto</option>
              </select>
              <label className="checkbox-row" style={{ whiteSpace: 'nowrap' }}>
                <input
                  type="checkbox"
                  checked={item.isRequired}
                  onChange={(event) => setItem(index, { isRequired: event.target.checked })}
                />
                Pflicht
              </label>
              <button className="btn sm ghost" onClick={() => move(index, -1)} disabled={index === 0} title="Nach oben">
                ↑
              </button>
              <button
                className="btn sm ghost"
                onClick={() => move(index, 1)}
                disabled={index === items.length - 1}
                title="Nach unten"
              >
                ↓
              </button>
              <button
                className="btn sm ghost"
                onClick={() => setItems((current) => current.filter((_, i) => i !== index))}
                title="Punkt entfernen"
              >
                <IconTrash width={13} height={13} />
              </button>
            </div>
          ))}
        </div>
        <div className="row">
          <button
            className="btn sm"
            onClick={() => setItems((current) => [...current, { label: '', inputType: 'check', isRequired: false }])}
          >
            <IconPlus width={13} height={13} /> Punkt hinzufügen
          </button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * Wartungskategorie.
 *
 * Die Vorschlagswerte für Intervall und Vorlauf sind der eigentliche Zweck:
 * Wer einen Plan anlegt, soll nicht nachschlagen müssen, ob Rauchmelder
 * jährlich oder alle zwei Jahre geprüft werden. Die Grundlage steht daneben,
 * damit die Zahl belegbar bleibt.
 */
function MaintenanceCategoryModal({
  category,
  onClose,
  onSaved,
}: {
  category?: any;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    key: category?.key ?? '',
    name: category?.name ?? '',
    description: category?.description ?? '',
    color: category?.color ?? '#2563eb',
    defaultIntervalUnit: category?.default_interval_unit ?? 'year',
    defaultIntervalValue: String(category?.default_interval_value ?? 1),
    defaultLeadDays: String(category?.default_lead_days ?? 14),
    legalBasis: category?.legal_basis ?? '',
    isActive: category ? !!category.is_active : true,
  });
  const [error, setError] = useState<unknown>(null);
  const set = (key: keyof typeof form, value: any) => setForm((current) => ({ ...current, [key]: value }));

  return (
    <Modal
      title={category ? `Wartungskategorie bearbeiten – ${category.name}` : 'Wartungskategorie anlegen'}
      onClose={onClose}
      size="wide"
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button
            className="btn primary"
            disabled={!form.key || form.name.length < 2}
            onClick={async () => {
              try {
                const payload = {
                  key: form.key,
                  name: form.name,
                  description: form.description || null,
                  color: form.color,
                  defaultIntervalUnit: form.defaultIntervalUnit,
                  defaultIntervalValue: Number(form.defaultIntervalValue),
                  defaultLeadDays: Number(form.defaultLeadDays),
                  legalBasis: form.legalBasis || null,
                  isActive: form.isActive,
                };
                if (category) await api.patch(`/api/catalogs/maintenance-categories/${category.id}`, payload);
                else await api.post('/api/catalogs/maintenance-categories', payload);
                onSaved();
              } catch (caught) {
                setError(caught);
              }
            }}
          >
            Speichern
          </button>
        </>
      }
    >
      <div className="stack">
        <ErrorMessage error={error} />
        <div className="form-grid">
          <Field label="Bezeichnung" required>
            <input
              value={form.name}
              autoFocus
              onChange={(event) =>
                setForm({
                  ...form,
                  name: event.target.value,
                  key: category
                    ? form.key
                    : form.key ||
                      event.target.value
                        .toLowerCase()
                        .replace(/[äöüß]/g, (c) => ({ 'ä': 'ae', 'ö': 'oe', 'ü': 'ue', 'ß': 'ss' })[c] ?? c)
                        .replace(/[^a-z0-9]+/g, '_')
                        .replace(/^_|_$/g, ''),
                })
              }
            />
          </Field>
          <Field label="Schlüssel" required hint={category ? 'Nach dem Anlegen unveränderlich.' : undefined}>
            <input value={form.key} disabled={!!category} onChange={(event) => set('key', event.target.value)} />
          </Field>
          <Field label="Farbe">
            <input value={form.color} onChange={(event) => set('color', event.target.value)} placeholder="#2563eb" />
          </Field>
          <Field label="Übliches Intervall" hint="Vorschlag für neue Pläne dieser Kategorie.">
            <div className="row" style={{ gap: 6, flexWrap: 'nowrap' }}>
              <input
                type="number"
                min="1"
                value={form.defaultIntervalValue}
                onChange={(event) => set('defaultIntervalValue', event.target.value)}
                style={{ width: 80 }}
              />
              <select
                value={form.defaultIntervalUnit}
                onChange={(event) => set('defaultIntervalUnit', event.target.value)}
              >
                <option value="day">Tage</option>
                <option value="week">Wochen</option>
                <option value="month">Monate</option>
                <option value="year">Jahre</option>
              </select>
            </div>
          </Field>
          <Field label="Vorlauf (Tage)" hint="So früh vor der Fälligkeit entsteht der Auftrag.">
            <input
              type="number"
              min="0"
              value={form.defaultLeadDays}
              onChange={(event) => set('defaultLeadDays', event.target.value)}
            />
          </Field>
          <Field label="Grundlage" hint="Norm oder Vorschrift, etwa DIN 14676 bei Rauchwarnmeldern.">
            <input value={form.legalBasis} onChange={(event) => set('legalBasis', event.target.value)} />
          </Field>
        </div>
        <Field label="Beschreibung">
          <input value={form.description} onChange={(event) => set('description', event.target.value)} />
        </Field>
        <Toggle checked={form.isActive} onChange={(value) => set('isActive', value)} label="Aktiv" />
      </div>
    </Modal>
  );
}

function unitLabel(unit: string, value: number): string {
  const map: Record<string, [string, string]> = {
    day: ['Tag', 'Tage'],
    week: ['Woche', 'Wochen'],
    month: ['Monat', 'Monate'],
    year: ['Jahr', 'Jahre'],
  };
  const entry = map[unit] ?? [unit, unit];
  return value === 1 ? entry[0] : entry[1];
}

/** Findet den Datensatz zu einer Kennung der Form `edit-<bereich>-<id>`. */
function findById(list: any[] | undefined, modal: string | null, prefix: string) {
  if (!modal?.startsWith(prefix)) return undefined;
  const id = modal.slice(prefix.length);
  return (list ?? []).find((entry: any) => String(entry.id) === id);
}

function inputTypeLabel(type: string): string {
  return { check: 'Haken', text: 'Text', number: 'Zahl', photo: 'Foto' }[type] ?? type;
}
